// ============================================
// 3. Operations.js - Complete Control Center
// ============================================

import React, { useState, useEffect } from 'react';

const Operations = () => {
  const [systemStatus, setSystemStatus] = useState({
    ibkr: { status: 'Connected', uptime: '99.8%', lastUpdate: '2025-08-03 09:15:22' },
    apis: { status: 'All Online', count: 8, errors: 0 },
    trading: { status: 'Active', positions: 3, orders: 0 },
    alerts: { status: 'Monitoring', active: 12, triggered: 2 }
  });

  const [emergencyMode, setEmergencyMode] = useState(false);
  const [confirmations, setConfirmations] = useState({});

  // Emergency Stop Functions
  const handleFullStop = () => {
    if (confirmations.fullStop) {
      setEmergencyMode(true);
      alert('🚨 EMERGENCY FULL STOP ACTIVATED - All trading halted globally');
      // This would trigger actual emergency stop
      setConfirmations({});
    } else {
      setConfirmations({...confirmations, fullStop: true});
      setTimeout(() => setConfirmations({}), 5000);
    }
  };

  const handleGlobalStop = () => {
    if (confirmations.globalStop) {
      alert('🌍 Global positions emergency stop - International markets halted');
      setConfirmations({});
    } else {
      setConfirmations({...confirmations, globalStop: true});
      setTimeout(() => setConfirmations({}), 5000);
    }
  };

  const handleDomesticStop = () => {
    if (confirmations.domesticStop) {
      alert('🇺🇸 Domestic positions emergency stop - US markets halted');
      setConfirmations({});
    } else {
      setConfirmations({...confirmations, domesticStop: true});
      setTimeout(() => setConfirmations({}), 5000);
    }
  };

  const handleRefreshData = () => {
    alert('🔄 Refreshing all data feeds - Market data, APIs, and positions updated');
    // This would trigger actual data refresh
  };

  const handleResetSystem = () => {
    if (confirmations.resetSystem) {
      setEmergencyMode(false);
      alert('🔄 System reset complete - All systems restored to normal operation');
      setConfirmations({});
    } else {
      setConfirmations({...confirmations, resetSystem: true});
      setTimeout(() => setConfirmations({}), 5000);
    }
  };

  const handleKillAllAlerts = () => {
    alert('🔕 All alerts disabled - Silent mode activated');
  };

  const handleForceSync = () => {
    alert('⚡ Force sync initiated - IBKR, Google Sheets, and all APIs syncing');
  };

  const handleTestMode = () => {
    alert('🧪 Test mode activated - All orders will be paper trades only');
  };

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      backgroundColor: '#1a2332',
      color: '#ffffff',
      fontFamily: 'Georgia, serif',
      padding: '24px'
    }}>
      {/* WATERMARK */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: '0.03',
        pointerEvents: 'none',
        zIndex: '1',
        width: '800px',
        height: '800px'
      }}>
        <img 
          src="/finance-watermark.jpg"
          alt="Financial Trading Watermark"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>

      <div style={{ position: 'relative', zIndex: '2', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              color: '#ffffff',
              fontSize: '32px',
              fontWeight: 'bold',
              margin: '0',
              fontFamily: 'Georgia, serif'
            }}>⚙️ Operations Control Center</h1>
            <p style={{
              color: '#a0a9c1',
              fontSize: '16px',
              margin: '8px 0 0 0',
              fontFamily: 'Georgia, serif'
            }}>System monitoring, emergency controls, and operational management</p>
          </div>
          {emergencyMode && (
            <div style={{
              backgroundColor: '#FF0000',
              color: '#ffffff',
              padding: '12px 20px',
              borderRadius: '8px',
              fontWeight: 'bold',
              animation: 'pulse 2s infinite',
              fontFamily: 'Georgia, serif'
            }}>
              🚨 EMERGENCY MODE ACTIVE
            </div>
          )}
        </div>

        {/* System Status Overview */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: systemStatus.ibkr.status === 'Connected' ? '#00FF5F' : '#FF0000'
              }}></div>
              <h3 style={{ color: '#D4AF37', margin: '0', fontFamily: 'Georgia, serif' }}>IBKR Connection</h3>
            </div>
            <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
              Status: {systemStatus.ibkr.status}<br/>
              Uptime: {systemStatus.ibkr.uptime}<br/>
              Last Update: {systemStatus.ibkr.lastUpdate}
            </div>
          </div>

          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: systemStatus.apis.errors === 0 ? '#00FF5F' : '#FF0000'
              }}></div>
              <h3 style={{ color: '#D4AF37', margin: '0', fontFamily: 'Georgia, serif' }}>API Services</h3>
            </div>
            <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
              Status: {systemStatus.apis.status}<br/>
              Active APIs: {systemStatus.apis.count}<br/>
              Errors: {systemStatus.apis.errors}
            </div>
          </div>

          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#00FF5F'
              }}></div>
              <h3 style={{ color: '#D4AF37', margin: '0', fontFamily: 'Georgia, serif' }}>Trading Status</h3>
            </div>
            <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
              Status: {systemStatus.trading.status}<br/>
              Open Positions: {systemStatus.trading.positions}<br/>
              Pending Orders: {systemStatus.trading.orders}
            </div>
          </div>

          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#FFB800'
              }}></div>
              <h3 style={{ color: '#D4AF37', margin: '0', fontFamily: 'Georgia, serif' }}>Alert System</h3>
            </div>
            <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
              Status: {systemStatus.alerts.status}<br/>
              Active Alerts: {systemStatus.alerts.active}<br/>
              Triggered Today: {systemStatus.alerts.triggered}
            </div>
          </div>
        </div>

        {/* Emergency Controls */}
        <div style={{
          backgroundColor: '#202A44',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b',
          marginBottom: '32px'
        }}>
          <h3 style={{ color: '#FF0000', margin: '0 0 20px 0', fontFamily: 'Georgia, serif' }}>
            🚨 Emergency Controls
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            <button
              onClick={handleFullStop}
              style={{
                backgroundColor: confirmations.fullStop ? '#FF0000' : '#660000',
                color: '#ffffff',
                border: '2px solid #FF0000',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              {confirmations.fullStop ? '⚠️ CONFIRM FULL STOP' : '🛑 EMERGENCY FULL STOP'}
            </button>

            <button
              onClick={handleGlobalStop}
              style={{
                backgroundColor: confirmations.globalStop ? '#FF6600' : '#663300',
                color: '#ffffff',
                border: '2px solid #FF6600',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              {confirmations.globalStop ? '⚠️ CONFIRM GLOBAL STOP' : '🌍 STOP GLOBAL POSITIONS'}
            </button>

            <button
              onClick={handleDomesticStop}
              style={{
                backgroundColor: confirmations.domesticStop ? '#FF9900' : '#664400',
                color: '#ffffff',
                border: '2px solid #FF9900',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              {confirmations.domesticStop ? '⚠️ CONFIRM DOMESTIC STOP' : '🇺🇸 STOP US POSITIONS'}
            </button>

            <button
              onClick={handleResetSystem}
              style={{
                backgroundColor: confirmations.resetSystem ? '#00FF5F' : '#004422',
                color: '#ffffff',
                border: '2px solid #00FF5F',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              {confirmations.resetSystem ? '⚠️ CONFIRM RESET' : '🔄 RESET SYSTEM'}
            </button>
          </div>
          {Object.keys(confirmations).length > 0 && (
            <div style={{
              color: '#FFB800',
              fontSize: '14px',
              marginTop: '12px',
              fontFamily: 'Georgia, serif',
              textAlign: 'center'
            }}>
              ⚠️ Click again within 5 seconds to confirm emergency action
            </div>
          )}
        </div>

        {/* Operational Controls */}
        <div style={{
          backgroundColor: '#202A44',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b',
          marginBottom: '32px'
        }}>
          <h3 style={{ color: '#0069B1', margin: '0 0 20px 0', fontFamily: 'Georgia, serif' }}>
            🔧 Operational Controls
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px'
          }}>
            <button
              onClick={handleRefreshData}
              style={{
                backgroundColor: '#0069B1',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold'
              }}
            >
              🔄 Refresh All Data
            </button>

            <button
              onClick={handleForceSync}
              style={{
                backgroundColor: '#5BC0DE',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold'
              }}
            >
              ⚡ Force Sync
            </button>

            <button
              onClick={handleKillAllAlerts}
              style={{
                backgroundColor: '#FFB800',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold'
              }}
            >
              🔕 Disable Alerts
            </button>

            <button
              onClick={handleTestMode}
              style={{
                backgroundColor: '#D4AF37',
                color: '#202A44',
                border: 'none',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold'
              }}
            >
              🧪 Test Mode
            </button>
          </div>
        </div>

        {/* System Logs */}
        <div style={{
          backgroundColor: '#202A44',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b'
        }}>
          <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
            📋 Recent System Activity
          </h3>
          <div style={{
            backgroundColor: '#1a2332',
            padding: '16px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '12px',
            color: '#00FF5F',
            height: '200px',
            overflowY: 'auto'
          }}>
            <div>[09:15:22] IBKR connection established - Portfolio sync complete</div>
            <div>[09:14:58] Government API update - 3 new congressional trades detected</div>
            <div>[09:14:45] Smart Score calculation complete - 15 symbols updated</div>
            <div>[09:14:12] Alert triggered: LMT price target reached (+2.8%)</div>
            <div>[09:13:55] Options chain refresh complete - NVDA, AAPL, TSLA updated</div>
            <div>[09:13:33] Quiver API sync - New contract award detected (PLTR)</div>
            <div>[09:13:21] Risk management check - Portfolio heat: 8.2% (SAFE)</div>
            <div>[09:12:45] Google Sheets update - Trade journal synced</div>
            <div>[09:12:12] Market data refresh - All symbols updated</div>
            <div>[09:11:55] System startup complete - All services online</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Operations;